/**
 * @file InstallPwaBanner.tsx
 * @summary Bandeau d'invitation à l'installation PWA (Android via beforeinstallprompt, iOS via instructions manuelles).
 */
import { useEffect, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

const DISMISS_DURATION_MS = 30 * 24 * 60 * 60 * 1000;
const SHOW_DELAY_MS = 11000;

export default function InstallPwaBanner() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    const ua = window.navigator.userAgent;
    const isIPhone =
      /iPhone|iPad|iPod/.test(ua) && !(window as Window & { MSStream?: unknown }).MSStream;
    const isAndroidDevice = /Android/i.test(ua);
    setIsIos(isIPhone);
    setIsAndroid(isAndroidDevice);

    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
    if (isStandalone) return;

    const dismissedAt = localStorage.getItem("chistera-pwa-dismissed-at");
    if (dismissedAt && Date.now() - parseInt(dismissedAt, 10) < DISMISS_DURATION_MS) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
      window.setTimeout(() => setShowBanner(true), SHOW_DELAY_MS);
    };
    window.addEventListener("beforeinstallprompt", handler);

    if (isIPhone) {
      window.setTimeout(() => setShowBanner(true), SHOW_DELAY_MS);
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    try {
      await installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      if (choice.outcome === "accepted") {
        setShowBanner(false);
        setInstallPrompt(null);
      }
    } catch (err) {
      console.error("PWA install error:", err);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem("chistera-pwa-dismissed-at", Date.now().toString());
  };

  if (!showBanner) return null;

  return (
    <div
      role="dialog"
      aria-label="Installer l'application CrossFit Chistera"
      className="fixed bottom-0 left-0 right-0 z-[9999] bg-charcoal text-white px-4 py-4 shadow-2xl border-t-2 border-gold"
      style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
    >
      <div className="max-w-4xl mx-auto flex items-start gap-3">
        <img
          src="/icons/icon-192.png"
          alt=""
          className="w-12 h-12 rounded-lg flex-shrink-0"
          width={48}
          height={48}
        />
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm leading-tight">Installer l'app CrossFit Chistera</p>
          <p className="text-xs text-white/70 mt-1 normal-case">
            {isIos ? (
              <>
                Appuyez sur{" "}
                <span className="inline-block bg-white/15 px-2 py-0.5 rounded text-white">
                  Partager ⬆️
                </span>{" "}
                puis{" "}
                <span className="inline-block bg-white/15 px-2 py-0.5 rounded text-white">
                  Sur l'écran d'accueil
                </span>
              </>
            ) : isAndroid && installPrompt ? (
              "Accès rapide, plein écran, hors-ligne. C'est gratuit."
            ) : (
              "Ajoutez le site à votre écran d'accueil pour un accès rapide."
            )}
          </p>
        </div>
        {!isIos && installPrompt && (
          <button
            onClick={handleInstall}
            className="bg-gold hover:bg-gold/90 text-charcoal font-bold px-4 py-2 rounded-lg text-sm whitespace-nowrap flex-shrink-0 transition-colors"
          >
            Installer
          </button>
        )}
        <button
          onClick={handleDismiss}
          aria-label="Fermer ce bandeau"
          className="text-white/60 hover:text-white p-2 flex-shrink-0 transition-colors"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
